import { useState, useEffect, useRef } from 'react';
import TopNav from '../components/TopNav';
import { walletApi } from '../services/api';
import { useApp } from '../AppContext';

const PRESET_AMOUNTS = [
  { amount: 5000, label: '~7 Standard orders' },
  { amount: 10000, label: '~14 Standard orders' },
  { amount: 25000, label: '~35 Standard orders' },
  { amount: 50000, label: '~70 Standard orders' },
  { amount: 100000, label: '~140 Standard orders' },
  { amount: 0, label: 'Custom' },
];

//const HDFC_METHOD = { id: 'hdfc', label: '🏦 HDFC Bank Transfer', desc: 'NEFT / RTGS — bank details provided after confirmation' };
const RAZOR_METHOD = { id: 'razorpay', label: '💳 Razorpay', desc: 'Pay online via UPI, Card, Net Banking' };

function buildPaymentMethods(paymentMethod) {
  if (!paymentMethod) return [ RAZOR_METHOD];//HDFC_METHOD,
  const pm = paymentMethod.toUpperCase();
  if (pm === 'BOTH') return [ RAZOR_METHOD];//HDFC_METHOD,
  if (pm === 'RAZOR') return [RAZOR_METHOD];
  //if (pm === 'HDFC') return [HDFC_METHOD];
  return [ RAZOR_METHOD];//HDFC_METHOD,
}

function buildAddress(details) {
  const lines = [
    details.billtoaddress1,
    details.billtoaddress2,
    details.billtoaddress3,
    details.billtoaddress4,
  ].filter(v => v && v.trim()).map(v => v.trim());

  const cityLine = [
    details.billcity,
    details.fullbillstate || details.billstate,
    details.billpostalcode,
  ].filter(Boolean).join(', ');

  if (cityLine) lines.push(cityLine);
  return lines.join(', ');
}

function calcTax(topupBase, details) {
  const isGujNonSez =
    details.economy_category === 'NON SEZ' &&
    details.billstate &&
    details.billstate.toUpperCase() === 'GJ';

  const CGST_RATE = parseFloat(process.env.REACT_APP_CGST_RATE) || 0.09;
  const SGST_RATE = parseFloat(process.env.REACT_APP_SGST_RATE) || 0.09;
  const IGST_RATE = parseFloat(process.env.REACT_APP_IGST_RATE) || 0.18;
  if (isGujNonSez) {
    const cgst = Math.round(topupBase * CGST_RATE);
    const sgst = Math.round(topupBase * SGST_RATE);
    return { cgst, sgst, igst: 0, isGujNonSez: true };
  }
  return { cgst: 0, sgst: 0, igst: Math.round(topupBase * IGST_RATE), isGujNonSez: false };
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    if (document.getElementById('razorpay-checkout-js')) { resolve(false); return; }
    const script = document.createElement('script');
    script.id = 'razorpay-checkout-js';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function Wallet({ onNavigate }) {
  const { addToast, setWalletBalance, setHoldBalance, setTotalBalance } = useApp();
  const [selectedAmt, setSelectedAmt] = useState(10000);
  const [customAmt, setCustomAmt] = useState('');
  const [method, setMethod] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const razorpayRef = useRef(null);

  useEffect(() => {
    walletApi.getWalletDetails()
      .then(data => {
        const record = Array.isArray(data) ? data[0] : data;
        setDetails(record);
        const cur = parseFloat(record.current_balance) || 0;
        const hld = parseFloat(record.hold_balance) || 0;
        setWalletBalance(cur);
        setHoldBalance(hld);
        setTotalBalance(cur + hld);
        const methods = buildPaymentMethods(record.paymentmethod);
        setMethod(methods[0].id);
      })
      .catch(e => addToast(e.message || 'Failed to load wallet details', 'error'))
      .finally(() => setLoading(false));

    loadRazorpayScript();

    return () => { if (razorpayRef.current) razorpayRef.current.close(); };
  }, []);

  const topupBase = selectedAmt || parseInt(customAmt) || 0;
  const { cgst, sgst, igst, isGujNonSez } = details
    ? calcTax(topupBase, details)
    : { cgst: 0, sgst: 0, igst: 0, isGujNonSez: false };
  // TDS is deducted on the Top-up Amount itself (not on the GST components) and comes
  // straight off the final payable amount — see WalletController.WalletDetails, which
  // sources the percentage from the appInvoiceTDS config key. Only applies when the client
  // has TDS enabled (CF_CLIENT.TDS_DEDUCTION = 'YES'); WalletDetails already zeroes the
  // percentage server-side for 'NO', but gate on the flag here too so the row-per-row
  // eligibility check isn't silently bypassed by a stale/mismatched percentage value.
  const tdsApplicable = details ? String(details.tds_deduction).toUpperCase() === 'YES' : false;
  const tdsPercentage = tdsApplicable ? (parseFloat(details.tds_percentage) || 0) : 0;
  const tdsAmount = Math.round(topupBase * tdsPercentage / 100);
  const totalPayable = topupBase + cgst + sgst + igst - tdsAmount;

  const currentBalance = details ? (parseFloat(details.current_balance) || 0) : 0;
  const holdBal = details ? (parseFloat(details.hold_balance) || 0) : 0;
  const totalBal = currentBalance + holdBal;
  const paymentMethods = details ? buildPaymentMethods(details.paymentmethod) : [ RAZOR_METHOD];//HDFC_METHOD,

  const clientName = details?.client_name || '';
  const gstin = details?.gstinofbill || '';
  const email = details?.invoice_copy_email || '';
  const phone = details?.mobile || details?.contact_number || details?.contact || '';
  const address = details ? buildAddress(details) : '';

  async function openCCAvenue() {
    const orderCreationId = `${Math.floor(100000 + Math.random() * 900000)}`;
    let res;
    try {
      res = await walletApi.initiateCCAvenue({ payable_amount: totalPayable, topup_amount: topupBase, orderCreationId });
    } catch (e) {
      addToast(e.message || 'Could not initiate HDFC payment. Please try again.', 'error');
      setSubmitting(false);
      return;
    }

    if (!res?.encRequest) {
      const errMsg = res?.message || res?.Message || 'Could not initiate HDFC payment.';
      console.error('InitiateCCAvenue error response:', res);
      addToast(errMsg, 'error');
      setSubmitting(false);
      return;
    }

    // Stash order details so App.jsx can rebuild the receipt after CCAvenue redirects back
    localStorage.setItem('ccavenue_pending', JSON.stringify({
      orderId: res.orderId || orderCreationId,
      topup_amount: topupBase,
      cgst_amount: cgst,
      sgst_amount: sgst,
      igst_amount: igst,
      payable_amount: totalPayable,
      clientName,
      gstin,
      email,
      currentBalance,
    }));

    // Build a hidden form and submit it to CCAvenue
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = res.actionUrl;

    const addField = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };
    addField('encRequest', res.encRequest);
    addField('access_code', res.accessCode);

    document.body.appendChild(form);
    form.submit();
  }

  async function openRazorpay() {
    const ready = await loadRazorpayScript();
    if (!ready || !window.Razorpay) {
      addToast('Razorpay could not load. Check your internet connection.', 'error');
      setSubmitting(false);
      return;
    }

    const orderCreationId = `ORD-RZP-${Date.now()}`;

    let razorpayOrderId = null;
    try {
      const orderRes = await walletApi.createRazorpayOrder({
        amount: totalPayable,
        orderCreationId,
        client_name: clientName,
        invoice_copy_email: email,
      });
      razorpayOrderId = orderRes.id;
    } catch (e) {
      addToast(e.message || 'Could not initiate payment. Please try again.', 'error');
      setSubmitting(false);
      return;
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: totalPayable * 100, // paise
      currency: 'INR',
      order_id: razorpayOrderId,
      name: 'cFIRST Background Chekcs LLP.',
      description: `Wallet Top-up — ${clientName}`,
      prefill: { name: clientName, email, contact: phone },
      notes: { client_name: clientName, gstin, topup_amount: topupBase, tax_amount: cgst + sgst + igst, order_creation_id: orderCreationId },
      theme: { color: '#f97316' },
      handler: async function (rzpResponse) {
        try {
          const payload = {
            razorpay_payment_id: rzpResponse.razorpay_payment_id,
            razorpay_order_id: rzpResponse.razorpay_order_id,
            razorpay_signature: rzpResponse.razorpay_signature,
            orderCreationId,
            topup_amount: topupBase,
            cgst_amount: cgst,
            sgst_amount: sgst,
            igst_amount: igst,
            payable_amount: totalPayable,
            paid_amount: totalPayable,
            client_name: clientName,
            gstinofbill: gstin,
            invoice_copy_email: email,
          };

          const result = await walletApi.proceedToRazorpay(payload);
          onNavigate('payment-receipt', {
            isSuccess: true,
            result,
            amounts: { topup_amount: topupBase, cgst_amount: cgst, sgst_amount: sgst, igst_amount: igst, payable_amount: totalPayable },
            clientInfo: { clientName, gstin, email },
            currentBalance,
          });
        } catch (e) {
          onNavigate('payment-receipt', {
            isSuccess: false,
            error: { description: e.message || 'Payment verification failed.' },
            amounts: { topup_amount: topupBase, cgst_amount: cgst, sgst_amount: sgst, igst_amount: igst, payable_amount: totalPayable },
            currentBalance,
          });
        } finally {
          setSubmitting(false);
        }
      },
      modal: {
        ondismiss: () => {
          setSubmitting(false);
          addToast('Payment cancelled. No amount was deducted.', 'error');
        },
      },
    };

    const rzp = new window.Razorpay(options);
    razorpayRef.current = rzp;
    rzp.on('payment.failed', (resp) => {
      setSubmitting(false);
      onNavigate('payment-receipt', {
        isSuccess: false,
        error: {
          description: resp.error.description,
          reason: resp.error.reason,
          code: resp.error.code,
          razorpay_payment_id: resp.error.metadata?.payment_id,
        },
        amounts: { topup_amount: topupBase, cgst_amount: cgst, sgst_amount: sgst, igst_amount: igst, payable_amount: totalPayable },
        currentBalance,
      });
    });
    rzp.open();
  }

  async function handlePay() {
    if (!topupBase || topupBase === 0) {
      addToast('Please select or enter an amount to top up.', 'error');
      return;
    }
    const MIN_TOPUP = parseInt(process.env.REACT_APP_MIN_TOPUP) || 2000;
    if (topupBase < MIN_TOPUP) {
      addToast(`Minimum top-up amount is ₹${MIN_TOPUP.toLocaleString('en-IN')}.`, 'error');
      return;
    }
    setSubmitting(true);
    if (method === 'razorpay') {
      await openRazorpay();
      return;
    }
    if (method === 'hdfc') {
      await openCCAvenue();
      return;
    }
    try {
      await walletApi.initiateTopup({ amount: topupBase, method });
      addToast('Top-up request initiated!', 'success');
    } catch (e) {
      addToast(e.message || 'Top-up failed', 'error');
    } finally { setSubmitting(false); }
  }

  if (loading) {
    return (
      <>
        <TopNav currentPage="wallet" onNavigate={onNavigate} />
        <div className="pg" style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
          <span className="spinner" />
        </div>
      </>
    );
  }

  return (
    <>
      <TopNav currentPage="wallet" onNavigate={onNavigate} />
      <div className="pg">
        <div className="phd">
          <div>
            <div className="ptitle">Add Credits to Wallet</div>
            <div className="psub">Prepaid top-up · Credits are deducted per BGV order placed</div>
          </div>
          <div className="otabs" style={{ marginBottom: 0, width: 350 }}>
            <div className="otab on" style={{ whiteSpace: 'nowrap' }} onClick={() => onNavigate('wallet')}>💰 Add Credits</div>
            <div className="otab" onClick={() => onNavigate('ledger')}>📒 Ledger</div>
            <div className="otab" onClick={() => onNavigate('invoices')}>📜 Invoices</div>
          </div>
        </div>

        {/* Banner */}
        <div className="wbanner wbanner-grid">
          <div className="wbanner-item">
            <div className="wbl">💰 Current Balance</div>
            <div className="wba" style={{ color: 'var(--or)' }}>₹{Number(currentBalance).toLocaleString('en-IN')}</div>
            <div className="wbc">Available to use</div>
          </div>
          <div className="wbanner-item">
            <div className="wbl">🔒 Hold Balance</div>
            <div className="wba" style={{ color: 'var(--am)', fontSize: '26px' }}>₹{Number(holdBal).toLocaleString('en-IN')}</div>
            <div className="wbc">Pending approvals</div>
          </div>
          <div className="wbanner-item">
            <div className="wbl">📊 Total Balance</div>
            <div className="wba" style={{ color: '#fff', fontSize: '26px' }}>₹{Number(totalBal).toLocaleString('en-IN')}</div>
            <div className="wbc">Current + Hold · {clientName}</div>
          </div>
        </div>

        <div className="gr2" style={{ gap: '22px', alignItems: 'start' }}>
          <div>
            {/* Amount selector */}
            <div className="card mb16">
              <div className="chd"><div className="ctitle">Select Top-up Amount</div></div>
              <div className="pgrid">
                {PRESET_AMOUNTS.map(p => (
                  <div key={p.amount}
                    className={`pbtn ${selectedAmt === p.amount && p.amount !== 0 ? 'on' : p.amount === 0 && selectedAmt === 0 ? 'on' : ''}`}
                    onClick={() => { setSelectedAmt(p.amount); if (p.amount !== 0) setCustomAmt(''); }}>
                    <div className="pamt">{p.amount === 0 ? 'Custom' : `₹${p.amount.toLocaleString('en-IN')}`}</div>
                    <div className="plbl">{p.label}</div>
                  </div>
                ))}
              </div>
              <div className="fg">
                <label className="flbl">Custom Amount (₹)</label>
                <input className="inp" placeholder="Min ₹2,000 — Max ₹5,00,000" value={customAmt}
                  onChange={e => { setCustomAmt(e.target.value); setSelectedAmt(0); }} />
                <div className="hint">Amounts are exclusive of GST. Applicable GST will be added at checkout.</div>
              </div>
            </div>

            {/* Payment method */}
            <div className="card">
              <div className="chd"><div className="ctitle">Payment Method</div></div>
              {paymentMethods.map(m => (
                <div key={m.id} className={`pmethod ${method === m.id ? 'on' : ''}`} onClick={() => setMethod(m.id)}>
                  <div style={{ width: '15px', height: '15px', borderRadius: '50%', border: `2px solid ${method === m.id ? 'var(--or)' : 'var(--g300)'}`, background: method === m.id ? 'var(--or)' : 'transparent', flexShrink: 0 }} />
                  <div>
                    <div className="pmlbl">{m.label}</div>
                    <div className="pmdesc">{m.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="card">
              <div className="chd"><div className="ctitle">Payment Summary</div></div>
              <div className="amtbox mb16">
                <div className="amtrow"><span className="aml">Top-up Amount</span><span className="amv">₹{topupBase.toLocaleString('en-IN')}</span></div>
                {isGujNonSez ? (
                  <>
                    <div className="amtrow"><span className="aml">CGST @ 9%</span><span className="amv">₹{cgst.toLocaleString('en-IN')}</span></div>
                    <div className="amtrow"><span className="aml">SGST @ 9%</span><span className="amv">₹{sgst.toLocaleString('en-IN')}</span></div>
                    <div className="amtrow"><span className="aml">IGST @ 0%</span><span className="amv">₹0</span></div>
                  </>
                ) : (
                  <>
                    <div className="amtrow"><span className="aml">CGST @ 0%</span><span className="amv">₹0</span></div>
                    <div className="amtrow"><span className="aml">SGST @ 0%</span><span className="amv">₹0</span></div>
                    <div className="amtrow"><span className="aml">IGST @ 18%</span><span className="amv">₹{igst.toLocaleString('en-IN')}</span></div>
                  </>
                )}
                {tdsAmount > 0 && (
                  <div className="amtrow"><span className="aml">{tdsPercentage}% TDS Deducted Without Taxable Amount</span><span className="amv">-₹{tdsAmount.toLocaleString('en-IN')}</span></div>
                )}
                <div className="amtrow"><span className="amtl">Total Payable</span><span className="amtv">₹{totalPayable.toLocaleString('en-IN')}</span></div>
              </div>
              <div className="flex jb aic mb8 ts"><span className="tg">Current balance</span><span className="f7">₹{Number(currentBalance).toLocaleString('en-IN')}</span></div>
              <div className="flex jb aic ts"><span className="tg">Balance after top-up</span><span className="f7 t-gr">₹{(Number(currentBalance) + topupBase).toLocaleString('en-IN')}</span></div>
              <div className="hr" />
              <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--nv)', marginBottom: '10px' }}>Billing Details</div>
              <div className="igrid mb16">
                <div className="iitem"><div className="ikey">Invoice To</div><div className="ival">{clientName}</div></div>
                <div className="iitem"><div className="ikey">GSTIN</div><div className="ival">{gstin}</div></div>
                <div className="iitem"><div className="ikey">Email</div><div className="ival">{email}</div></div>
                {address && <div className="iitem"><div className="ikey">Address</div><div className="ival">{address}</div></div>}
                <div className="iitem"><div className="ikey">Phone</div><div className="ival">{phone}</div></div>
              </div>
              <div className="alert a-bl mb16">
                <div className="aico">ℹ️</div>
                <div className="abody" style={{ fontSize: '11px' }}>A GST invoice (cFIRST Background Chekcs LLP.) will be emailed to your accounts email within 24 hours of payment confirmation.</div>
              </div>
              <button className="btn bpri wf blg" onClick={handlePay} disabled={submitting}>
                {submitting
                  ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Processing…</>
                  : method === 'razorpay'
                    ? `Pay ₹${totalPayable.toLocaleString('en-IN')} via Razorpay →`
                    : method === 'hdfc'
                      ? `Pay ₹${totalPayable.toLocaleString('en-IN')} via HDFC / CCAvenue →`
                      : `Proceed to Pay ₹${totalPayable.toLocaleString('en-IN')} →`}
              </button>
              <button className="btn bghost wf mt8" onClick={() => onNavigate('dashboard')} disabled={submitting}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
